
import { useMemo } from 'react';
import { Client } from '@/components/clients/types';
import { Lead } from '@/components/leads/types';
import { EnrichmentResponse } from '@/hooks/useCompanyEnrichment';
import { 
  EnrichmentDialogState, 
  EnrichedData, 
  BackendProgressStage,
  MappableClientField 
} from '@/components/clients/enrichment-dialog/enrichmentTypes';
import { assertEnrichedData } from '@/utils/typeGuards';

interface UseEnrichmentDataProcessorProps {
  client?: Client;
  lead?: Lead;
  enrichmentResult: Partial<EnrichmentResponse> | null;
  mappableFields: Array<MappableClientField<any>>;
}

export function useEnrichmentDataProcessor({
  client,
  lead,
  enrichmentResult,
  mappableFields,
}: UseEnrichmentDataProcessorProps) {
  const entity = client || lead;
  
  // Derive dialog state from enrichment result
  const dialogState: EnrichmentDialogState = useMemo(() => {
    if (!enrichmentResult) return 'loading';
    
    const status = enrichmentResult.status || 'pending';
    const progressStage = enrichmentResult.progress_stage;
    
    // Map status and progress stage to dialog state
    if (status === 'completed') return 'success';
    if (status === 'error' || status === 'failed') {
      if (progressStage?.includes('error_')) return progressStage as EnrichmentDialogState;
      return 'error_unknown';
    }
    if (status === 'cancelled') return 'cancelled';
    if (status === 'applied') return 'applied';
    
    // Handle in-progress states
    if (progressStage) {
      return progressStage as EnrichmentDialogState;
    }
    
    return 'loading';
  }, [enrichmentResult]);

  // Extract enriched company data
  const actualCompanyData: EnrichedData | null = useMemo(() => {
    if (!enrichmentResult?.enriched_data) return null;
    return assertEnrichedData(enrichmentResult.enriched_data, 'enrichmentResult.enriched_data');
  }, [enrichmentResult]);

  // Initialize selected fields state
  const selectedFields: Record<string, boolean> = useMemo(() => {
    const fields: Record<string, boolean> = {};
    mappableFields.forEach(field => {
      fields[field.key as string] = false;
    });
    return fields;
  }, [mappableFields]);

  // Handle field toggle
  const handleToggleField = (key: string) => {
    // This would normally update state, but for now just log
    console.log('Toggle field:', key);
  };

  // Derive message from enrichment result
  const derivedMessage = useMemo(() => {
    if (!enrichmentResult) return 'Initiating enrichment...';
    
    const status = enrichmentResult.status;
    const progressStage = enrichmentResult.progress_stage;
    
    if (status === 'completed') return 'Enrichment completed successfully';
    if (status === 'error' || status === 'failed') {
      return enrichmentResult.error_message || 'Enrichment failed';
    }
    if (status === 'cancelled') return 'Enrichment was cancelled';
    
    // Progress stage messages
    if (progressStage === 'processing') return 'Processing enrichment data...';
    if (progressStage === 'extracting') return 'Extracting company information...';
    if (progressStage === 'finalizing') return 'Finalizing enrichment results...';
    
    return 'Enrichment in progress...';
  }, [enrichmentResult]);

  // Extract run ID
  const derivedLutraRunId = enrichmentResult?.run_id || null;

  // Extract error details
  const errorDetails = useMemo(() => {
    if (!enrichmentResult?.error_message) return null;
    return {
      error: enrichmentResult.error_message,
      details: enrichmentResult.enriched_data || null
    };
  }, [enrichmentResult]);

  // Raw debug response
  const rawDebugResponse = useMemo(() => {
    return assertEnrichedData(enrichmentResult?.enriched_data, 'rawDebugResponse');
  }, [enrichmentResult]);

  // Initial Lutra response (same as enriched data for now)
  const initialLutraResponse = actualCompanyData;

  // Backend progress stage
  const backendProgressStage: BackendProgressStage | string | null = 
    enrichmentResult?.progress_stage || null;

  // Estimated completion time
  const estimatedCompletionAt = enrichmentResult?.estimated_completion_at || null;

  return {
    dialogState,
    actualCompanyData,
    selectedFields,
    handleToggleField,
    derivedMessage,
    derivedLutraRunId,
    errorDetails,
    rawDebugResponse,
    initialLutraResponse,
    backendProgressStage,
    estimatedCompletionAt,
  };
}
